import { useCallback, useEffect, useRef, useState } from "react";
import { clockOffsetMs } from "radio-shared";
import { api } from "../api";

/**
 * Décalage entre l'horloge du navigateur et celle du serveur. Tout le reste de la
 * synchronisation en dépend : sans lui, un poste dont l'horloge dérive de 10 s
 * écouterait la radio 10 s en avance.
 *
 * On mesure plusieurs allers-retours et on garde le plus rapide : c'est celui qui
 * a le moins de latence à répartir, donc le moins de bruit.
 */
export function useClock(): { serverNow: () => number; ready: boolean } {
  const offset = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      let best = { rtt: Number.POSITIVE_INFINITY, offset: 0 };
      for (let i = 0; i < 3; i++) {
        const sentAt = Date.now();
        try {
          const { serverMs } = await api.time();
          const receivedAt = Date.now();
          const rtt = receivedAt - sentAt;
          if (rtt < best.rtt) best = { rtt, offset: clockOffsetMs(sentAt, serverMs, receivedAt) };
        } catch {
          // une mesure ratée n'est pas grave, il en reste d'autres
        }
      }
      if (!cancelled && Number.isFinite(best.rtt)) {
        offset.current = best.offset;
        setReady(true);
      }
    };

    void sync();
    // l'horloge d'une machine qui sort de veille peut avoir sauté
    const timer = setInterval(() => void sync(), 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // Référence stable : recréer cette fonction à chaque render relancerait tous
  // les effets qui en dépendent — et annulerait les chargements en cours.
  const serverNow = useCallback(() => Date.now() + offset.current, []);

  return { serverNow, ready };
}
